"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useThrottledCallback } from "use-debounce";
import "mapbox-gl/dist/mapbox-gl.css";
import { default as ReactMapGl, LayerProps, useMap } from "react-map-gl/mapbox";
import { MapMouseEvent, LayerSpecification, SourcesSpecification, GeoJSONFeature } from "mapbox-gl";
import { useAppStore } from "@/store/app";
import { useDataStore } from "@/store/data";
import { getEventFeature } from "../utils/helpers";
import { getMapLayer, getMapLayers, getMapStaticLayer, getSourceIds, getSourceUris } from "../utils/selectors";
import { MAP_DEFAULT_PROPS, MAP_DEFAULT_PADDING_MOBILE, MAP_THROTTLE_DURATION } from "../utils/constants";
import MAPBOX_STYLE_JSON from "../utils/style.json";
import styles from "../Map.module.css";

export interface MapBaseProps {
	onMove: () => void;
	onMouseMove?: (
		properties?: GeoJSONFeature["properties"],
		point?: MapMouseEvent["point"]
	) => void;
	onMouseLeave: () => void;
	onMouseOut: () => void;
	onClick: (
		properties?: GeoJSONFeature["properties"]
	) => void;
	onLoad: () => void;
};

export default function MapBase({
	onMove,
	onMouseMove,
	onMouseLeave,
	onMouseOut,
	onClick,
	onLoad
} : MapBaseProps) {
	const { default: map } = useMap();
	const controls = useAppStore(store => store.controls);
	const extents = useDataStore(store => store.extents);
	const getExtents = useDataStore(store => store.getExtents);
	const [mapProps, setMapProps] = useState<any>(MAP_DEFAULT_PROPS);
	const [mapLoaded, setMapLoaded] = useState<boolean>(false);
	const [sourceLoaded, setSourceLoaded] = useState<boolean>(false);
	const [patternLoaded, setPatternLoaded] = useState<boolean>(false);
	const [loadedSources, setLoadedSources] = useState<string[]>([]);

	const sourceIds = useMemo(() =>
		getSourceIds(controls)
	, [controls]);

	const sourceUris = useMemo(() =>
		getSourceUris(controls)
	, [controls]);

	// Object containing all sources to load on the map
	const mapSources: SourcesSpecification = useMemo(() => {
		const defaultMapSources = MAPBOX_STYLE_JSON.sources;
		return {
			...defaultMapSources,
			...sourceIds.reduce((obj, id, index) => ({
				...obj,
				[id]: {
					"promoteId": "id",
					"type": "vector",
					"url": `${sourceUris[index]}`
				}
			}), {})
		} as any;
	}, [sourceIds, sourceUris]);

	// Map layers
	const mapLayers: LayerSpecification[] = useMemo(() => {
		if(!map || !mapLoaded || !sourceLoaded || !patternLoaded) return [];
		const defaultMapLayers: LayerSpecification[] = MAPBOX_STYLE_JSON.layers as any;
		const newMapLayers = [
			...defaultMapLayers,
		] as any;
		const bodyStyles = typeof window !== "undefined"
			? window?.getComputedStyle(document.body)
			: null;
		const mapExtents = getExtents(controls.geo, controls.view, controls.stat);
		// Get view colors from CSS variables to use as choropleth
		const mapColors = mapExtents?.map((value, index) =>
			bodyStyles?.getPropertyValue(`--color-${controls.view}-choropleth-${index + 1}`) || "transparent",
		);
		const mapLandColor = bodyStyles?.getPropertyValue(`--color-${controls.view}-map-land`) || "transparent"
		const mapWaterColor = bodyStyles?.getPropertyValue(`--color-${controls.view}-map-water`) || "transparent";
		const landLayerIndex = newMapLayers.findIndex((layer: LayerProps) => layer.id === "land");
		const waterLayerIndex = newMapLayers.findIndex((layer: LayerProps) => layer.id === "water");
		if(newMapLayers[landLayerIndex] && newMapLayers[landLayerIndex].paint) {
			newMapLayers[landLayerIndex].paint["background-color"] = mapLandColor;
		}
		if(newMapLayers[waterLayerIndex] && newMapLayers[waterLayerIndex].paint) {
			newMapLayers[waterLayerIndex].paint["fill-color"] = mapWaterColor;
		}
		// Parameters to get passed to map layer generator
		const mapParams = {
			...controls,
			colors: mapColors,
			extents: mapExtents,
		};
		/* When map and data source is loaded, splice in data layers before first layer of admin boundaries */
		if(mapLoaded && sourceLoaded) {
			const firstBoundaryLayerIndex = newMapLayers.findIndex((layer: LayerProps) =>
				// layer.type === "symbol"
				layer.id === "admin-1-boundary-bg"
			);
			const mapStateLayer = getMapStaticLayer("state");
			newMapLayers.splice(firstBoundaryLayerIndex, 0, mapStateLayer);
			const mapLayerIds = Object.keys(getMapLayers(mapParams));
			mapLayerIds.map((key: string) => {
				const mapLayer = getMapLayer(key, mapParams);
				newMapLayers.splice(firstBoundaryLayerIndex, 0, mapLayer);
			});
		}
		return newMapLayers;
	}, [map, controls, extents, getExtents, mapLoaded, sourceLoaded, patternLoaded]);
	
	// Map styles
	const mapStyle = useMemo(() => {
		return {
			...MAPBOX_STYLE_JSON,
			sources: mapSources,
			layers: mapLayers
		} as any;
	}, [mapSources, mapLayers]);

	useEffect(() => {
		map?.loadImage("/images/pattern.png", (err, image: any) => {
			if (err) throw err;
			map.addImage("pattern", image, {
				pixelRatio: 14
			});
			setPatternLoaded(true);
		})
	}, [map]);

	// On move (pan, zoom) of map
	const handleMove = useCallback(() => {
		if(onMove) onMove();
	}, [onMove]);

	// On mouse move on map
	const handleMouseMove = useThrottledCallback((event: MapMouseEvent) => {
		// Get feature properties from event
		const feature = getEventFeature(event);
		const properties = feature?.properties;
		// Store hovered point for preview tooltip placement
		const { point } = event;
		if(onMouseMove) onMouseMove(properties, point);
		// Add pointer cursor when hovering interactive feature
		if(properties) event.target.getCanvas().style.cursor = "pointer";
	}, MAP_THROTTLE_DURATION);

	// On mouse leave from map
	const handleMouseLeave = useThrottledCallback((event: MapMouseEvent) => {
		// Clear hover values when leaving interactive feature
		if(onMouseLeave) onMouseLeave();
		// Remove pointer cursor when leaving interactive feature
		event.target.getCanvas().style.cursor = "";
	}, MAP_THROTTLE_DURATION);

	// On mouse out from map
	const handleMouseOut = useThrottledCallback((event: MapMouseEvent) => {
		// Clear hover values when leaving interactive feature
		if(onMouseOut) onMouseOut();
		// Remove pointer cursor when leaving interactive feature
		event.target.getCanvas().style.cursor = "";
	}, MAP_THROTTLE_DURATION);

	const handleClick = useThrottledCallback((event: MapMouseEvent) => {
		// Get feature properties from event
		const feature = getEventFeature(event);
		const properties = feature?.properties;
		if(onClick) onClick(properties);
	}, MAP_THROTTLE_DURATION);

	const handleMapLoad = () => setMapLoaded(true);
	
	const handleSourceLoad = (event: any) => {
		const { sourceId, sourceDataType, isSourceLoaded } = event;
		// If source is loaded and matches custom source ID
		if(isSourceLoaded && sourceDataType === "content" && sourceIds.includes(sourceId)) {
			setLoadedSources(arr => [
				...arr,
				sourceId
			])
		}
	};

	useEffect(() => {
		if(loadedSources.length === sourceIds.length) setSourceLoaded(true);
	}, [loadedSources]);

	useEffect(() => {
		if(mapLoaded && sourceLoaded && patternLoaded) onLoad();
	}, [mapLoaded, sourceLoaded, patternLoaded]);

	useEffect(() => {
		// If mobile size, use different padding
		const newMapProps = MAP_DEFAULT_PROPS
		if(window.innerWidth <= 768) {
			newMapProps.initialViewState.fitBoundsOptions.padding = MAP_DEFAULT_PADDING_MOBILE
			setMapProps(newMapProps)
		}
	}, []);
	
	return (
		<div
			className={styles.MapBase}
		>
			<ReactMapGl
				// interactive={interactive}
				mapStyle={mapStyle}
				onMove={handleMove}
				onMouseMove={handleMouseMove}
				onMouseLeave={handleMouseLeave}
				onMouseOut={handleMouseOut}
				onClick={handleClick}
				onLoad={handleMapLoad}
				onSourceData={handleSourceLoad}
				{...mapProps}
				// {...overrides}
			/>
		</div>
	);
}